
import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react";
import MessageBridge, { MessageTypes } from "Utils/MessageBridge";
import userDetail from "Stores/userDetail";
import Utils from "Utils/utils";

const Shell = observer(() => {
  const iframeRef = useRef(null);
  const [initialUrl] = useState(() => {
    // Construct the App URL. 
    // If we are at /dashboard, iframe src should be /dashboard?view=app
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set("view", "app");
    return currentUrl.toString();
  });

  useEffect(() => {
    // Shutdown Intercom in the Shell so it only appears inside the main-app iframe
    if (window.Intercom) {
      window.Intercom('shutdown');
    }

    // Listen for messages from Iframe
    const cleanup = MessageBridge.listen({
      [MessageTypes.SYNC_URL]: (payload) => {
        if (payload?.path) {
          // Update the browser URL without reloading
          // We remove '?view=app' from the visible URL for cleanliness
          const newPath = payload.path;
          window.history.replaceState(null, "", newPath);
        }
      },
      [MessageTypes.SYNC_USER]: (payload = {}) => {
        const { email = "" } = payload || {};
        if (!email) return;
        // Mirror the iframe's user context into the Shell's own stores, then
        // reuse the app's identify routine. After this, Utils.mixpanelTrack in
        // the Shell auto-fills userEmail/planType/signupDate + distinct_id.
        userDetail.setUserInfo({ email });
        userDetail.setUserFeatureFlag({
          createTs: payload.createTs || "",
          planName: payload.planName || "",
          inFreePlan: Boolean(payload.inFreePlan)
        });
        if (window.mixpanel) Utils.registerMixpanel();
      }
    });

    return cleanup;
  }, []);

  useEffect(() => {
    // Bridge Chrome extension messages → iframe (app)
    //
    // The Chrome extension content script posts messages to the TOP-LEVEL window.
    // Since the app runs inside an iframe, it cannot receive these messages directly.
    // This listener catches all extension messages at the Shell level and
    // forwards them into the iframe so chromeExtValidator.js can handle them.
    const EXTENSION_MESSAGE_TYPES = new Set([
      "AE_PLUGIN_INSTALLED",
      "LINKEDIN_CONNECT_PLUGIN_INSTALLED",
      "LINKEDIN_COOKIES_AT_PLUGIN",
      "LINKEDIN_COOKIES_A_PLUGIN",
      "LINKEDIN_PROFILE_DATA_PLUGIN",
    ]);

    const forwardExtensionMessages = (event) => {
      const type = event?.data?.type;
      if (type && EXTENSION_MESSAGE_TYPES.has(type)) {
        if (iframeRef.current && iframeRef.current.contentWindow) {
          iframeRef.current.contentWindow.postMessage(event.data, "*");
        }
      }
    };

    window.addEventListener("message", forwardExtensionMessages);
    return () => window.removeEventListener("message", forwardExtensionMessages);
  }, []);

  return (
    <iframe
      ref={iframeRef}
      id="main-app"
      title="Salesgear App"
      src={initialUrl}
      style={{ width: "100%", height: "100vh", border: "none", display: "block" }}
    />
  );
});

export default Shell;
